// InterventionContextSpec — 19BO+ Line 2 B2 · AI 多点介入统一抽象
//
// 为什么存在 (llm-native.md §12.2 铁律 1):
//   §1–§11 只描述「生成/重生」两类 LLM 介入，但真实学习周期还有评估/诊断/解释
//   三类，它们的 context 来源根本不同（评估要用户答案+rubric；诊断要错误模式；
//   解释要关联 items）。历史上 RegenContextSpec 只覆盖 regenerate 一类。
//   本 schema 把五类介入的 context 契约统一到一个判别联合，让 gather 函数 / endpoint
//   / replay 工具 / 计费都基于同一抽象——禁止 view 层或 endpoint 层自由拼接 prompt
//   context（那是 §9 反模式：prompt 散落多文件）。
//
// 为什么是判别联合 (z.discriminatedUnion on 'kind'):
//   五类介入的 needs 形状不同（evaluate 需要 userAnswer/rubric · diagnose 需要
//   wrongAnswerAggregate · explain 需要 currentItem）。判别联合让 per-kind 形状
//   编译期固定——evaluate 缺 rubric 会在 parse 时报错，而不是 drift 到 prompt
//   构造时才暴露。这与 RegenActionSchema (regen-action.ts:116) 的设计同构。
//
// acceptsFeedback 的语义 (Tension 1 · 2026-06-26 裁定「能力进 spec / 数据进 action」):
//   feedback 的本质是「这次动作的即时输入」，不是「这类介入的固定配方」。所以
//   feedback 实例数据仍走 RegenAction.userFeedback (零破坏现有 regen 路径)；
//   InterventionContextSpec 只声明「该 kind 是否接受 feedback」(per-kind 固定 literal)。
//   replay 保真靠 promptVersions hash 输入域 = spec hash + action.userFeedback 双路
//   (llm-native.md §6.2)，spec 不是 100% 自包含是此裁定的已知代价。
//     - regenerate: literal(true)  — regen 天然接受用户 hint
//     - 其余四类:    literal(false) — 生成/评估/诊断/解释当前无 feedback 通道
//   若未来某 kind 需运行时切换，再放宽为 z.boolean()。
//
// privacyBoundary 的语义 (引用 §13.4 同步边界目标表):
//   「该介入需要读写的数据所属边界（目标态）」。local_only = IDB 本地；
//   user_scoped_cloud = D1 云端按用户隔离。DEFAULT 提供每 kind 的目标默认值，
//   但允许 caller 覆盖（同 kind 在不同场景可能不同边界）。
//
// 扩展契约 (加第 6 类介入动作 · §12.1 五类是当前全集):
//   1. 加 INTERVENTION_KIND_VALUES + InterventionKindSchema
//   2. 加 NeedsSchema + InterventionSchema variant
//   3. 加 DEFAULT_INTERVENTION_CONTEXT_SPEC 条目
//   4. 加进 discriminatedUnion 数组
//   4 处全在本文件，零 endpoint 改动（B3/B4 endpoint 按 kind switch）。
import { z } from 'zod';
import { RegenContextSpecSchema } from './regen-context-spec.js';
import { DEFAULT_REGEN_CONTEXT_SPEC } from './regen-context-spec.js';
// ─── kind 判别值 + privacy 边界枚举 ──────────────────────────────────────────
/**
 * 5 类介入动作（按 context 来源分类，不按 AI 能力分类 — §12.1 核心断言）。
 * 顺序固定：generate（一次性建内容）→ regenerate（重生既有）→ evaluate（评判）
 * → diagnose（归因）→ explain（解释单点）。这是学习周期里的介入发生顺序。
 */
export const INTERVENTION_KIND_VALUES = [
    'generate',
    'regenerate',
    'evaluate',
    'diagnose',
    'explain',
];
export const InterventionKindSchema = z.enum(INTERVENTION_KIND_VALUES);
/**
 * 介入数据的同步边界（引用 §13.4 目标表）。local_only = IDB 纯本地；
 * user_scoped_cloud = D1 按用户隔离云端。
 */
export const PRIVACY_BOUNDARY_VALUES = ['local_only', 'user_scoped_cloud'];
export const PrivacyBoundarySchema = z.enum(PRIVACY_BOUNDARY_VALUES);
// ─── per-kind Needs schemas ──────────────────────────────────────────────────
/**
 * Generate 介入的 context 需求。
 *
 * ⚠ forward-looking 契约（§12.1 已记 drift）：generate 现状是去中心化的——
 * ingest-start 时 StageContext 一次性装配，per-stage buildPromptForChapter 各自切片
 * （per-chapter-runner.ts:441-476），不走集中式 gather。本 needs 是「未来对齐契约」，
 * 待 generate 管线下次大改时收敛为集中式 gather 才会真有消费者。B2 只声明形状。
 */
const GenerateNeedsSchema = z.object({
    /** 生成始终需要 frozen full outline 作 anchor（§7 三段 · Pillar B）。v4 固定 true。 */
    fullOutline: z.literal(true),
    /** 是否需要 chapter-level 源材料（PDF/讲义切片）。 */
    chapterSource: z.boolean(),
    /** 是否注入学科 bias（§discipline-taxonomy · DISCIPLINE_BIASES）。 */
    disciplineBias: z.boolean(),
}).strict();
/**
 * Regenerate 介入的 context 需求——直接复用 RegenContextSpec（DRY）。
 *
 * RegenContextSpec 自 19BH Day 2 稳定（六布尔 + 同 stage 枚举），regenerate variant
 * 的 needs 就是它的别名。若 RegenContextSpec 未来加字段，本 variant 自动跟变——
 * DEFAULT 工厂 + 单测 pin 此 lockstep 契约（regenerate kind contract reuse test）。
 */
const RegenerateNeedsSchema = RegenContextSpecSchema;
/**
 * Evaluate 介入的 context 需求（评估用户答案 · 当前 stub · B3 实装）。
 * context 来源：用户答案（human_input）+ 题目 + 参考答案 rubric + 历史 attempt。
 */
const EvaluateNeedsSchema = z.object({
    /** 用户提交的答案（human_input provenance · §2.1）。 */
    userAnswer: z.boolean(),
    /** 题目本身（题干 + 选项/空位）。 */
    question: z.boolean(),
    /** 参考答案（如有）。 */
    referenceAnswer: z.boolean(),
    /** 评分 rubric（维度 + 权重 + band 描述）。 */
    rubric: z.boolean(),
    /** 历史 attempt（跨设备诊断需云化 · §13.4 目标 user_scoped_cloud）。 */
    historyAttempts: z.boolean(),
}).strict();
/**
 * Diagnose 介入的 context 需求（弱项诊断 · 当前纯本地统计无 AI · B4 实装）。
 * context 来源：wrongAnswers 聚合 + 题目元数据 + 时间序列。
 */
const DiagnoseNeedsSchema = z.object({
    /** wrongAnswers 聚合（按 chapter/topic/concept 分布）。 */
    wrongAnswerAggregate: z.boolean(),
    /** 题目元数据（chapter/topic/cognitiveLevel · Bloom 归因用）。 */
    itemMetadata: z.boolean(),
    /** 时间序列（掌握度随时间变化 · 趋势判断用）。 */
    timeSeries: z.boolean(),
}).strict();
/**
 * Explain 介入的 context 需求（解释/关联单点 · 当前无 · B3/B4 实装）。
 * context 来源：当前 item + 关联 items + outline context。
 */
const ExplainNeedsSchema = z.object({
    /** 当前要解释的 item。 */
    currentItem: z.boolean(),
    /** 关联 items（同 concept/topic 的其它题 · 帮助理解）。 */
    relatedItems: z.boolean(),
    /** outline context（当前 item 在课程结构里的位置）。 */
    outlineContext: z.boolean(),
}).strict();
// ─── per-kind Intervention variants (discriminated union members) ───────────
const GenerateInterventionSchema = z.object({
    kind: z.literal('generate'),
    needs: GenerateNeedsSchema,
    privacyBoundary: PrivacyBoundarySchema,
    acceptsFeedback: z.literal(false),
}).strict();
const RegenerateInterventionSchema = z.object({
    kind: z.literal('regenerate'),
    needs: RegenerateNeedsSchema,
    privacyBoundary: PrivacyBoundarySchema,
    acceptsFeedback: z.literal(true),
}).strict();
const EvaluateInterventionSchema = z.object({
    kind: z.literal('evaluate'),
    needs: EvaluateNeedsSchema,
    privacyBoundary: PrivacyBoundarySchema,
    acceptsFeedback: z.literal(false),
}).strict();
const DiagnoseInterventionSchema = z.object({
    kind: z.literal('diagnose'),
    needs: DiagnoseNeedsSchema,
    privacyBoundary: PrivacyBoundarySchema,
    acceptsFeedback: z.literal(false),
}).strict();
const ExplainInterventionSchema = z.object({
    kind: z.literal('explain'),
    needs: ExplainNeedsSchema,
    privacyBoundary: PrivacyBoundarySchema,
    acceptsFeedback: z.literal(false),
}).strict();
/**
 * AI 多点介入统一抽象（§12.2 铁律 1 的可执行落地）。
 *
 * 五类介入的 context 契约统一到此 schema。gather 函数（B3 的 gatherInterventionContext
 * · 待实装）、endpoint（B3 的 /ingest/evaluate · B4 的 /ingest/diagnose · 待实装）、
 * replay 工具、计费（B3 的 consume_evaluate reason · 待实装）都基于此抽象。
 *
 * 当前消费者：无（B2 只落地 schema + 单测；endpoint 接入是 B3/B4）。
 */
export const InterventionContextSpecSchema = z.discriminatedUnion('kind', [
    GenerateInterventionSchema,
    RegenerateInterventionSchema,
    EvaluateInterventionSchema,
    DiagnoseInterventionSchema,
    ExplainInterventionSchema,
]);
/**
 * 每 kind 的默认 spec。privacyBoundary 用 §13.4 目标表的默认值；needs 用全 true
 * （保守起点：声明需要所有可能相关的 context，gather 函数按实际数据可用性降级）。
 *
 * 覆盖契约：caller 可基于 DEFAULT 覆盖单字段（如 evaluate 介入暂不要 historyAttempts
 * 时 `{ ...DEFAULT.evaluate, needs: { ...DEFAULT.evaluate.needs, historyAttempts: false } }`）。
 */
export const DEFAULT_INTERVENTION_CONTEXT_SPEC = {
    generate: {
        kind: 'generate',
        needs: { fullOutline: true, chapterSource: true, disciplineBias: true },
        privacyBoundary: 'user_scoped_cloud',
        acceptsFeedback: false,
    },
    regenerate: {
        kind: 'regenerate',
        needs: DEFAULT_REGEN_CONTEXT_SPEC,
        privacyBoundary: 'user_scoped_cloud',
        acceptsFeedback: true,
    },
    evaluate: {
        kind: 'evaluate',
        needs: {
            userAnswer: true,
            question: true,
            referenceAnswer: true,
            rubric: true,
            historyAttempts: true,
        },
        privacyBoundary: 'user_scoped_cloud',
        acceptsFeedback: false,
    },
    diagnose: {
        kind: 'diagnose',
        needs: { wrongAnswerAggregate: true, itemMetadata: true, timeSeries: true },
        privacyBoundary: 'user_scoped_cloud',
        acceptsFeedback: false,
    },
    explain: {
        kind: 'explain',
        needs: { currentItem: true, relatedItems: true, outlineContext: true },
        privacyBoundary: 'local_only',
        acceptsFeedback: false,
    },
};
//# sourceMappingURL=intervention-context.js.map